import React, { useEffect, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { GlobalButton } from './GlobalComponents/GlobalButton';

interface GlobalDeleteModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  loading?: boolean;
  title?: string;
  subtitle?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  // Optional: custom content to show above confirm buttons (e.g. list of items)
  children?: React.ReactNode;
}

const GlobalDeleteModal: React.FC<GlobalDeleteModalProps> = ({
  open,
  onClose,
  onConfirm,
  loading = false,
  title = 'Delete Item',
  subtitle = 'Are you sure you want to delete this item? This action cannot be undone.',
  confirmLabel = 'Delete',
  cancelLabel = 'Cancel',
  children,
}) => {
  const [isClosing, setIsClosing] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  useEffect(() => {
    if (!open) {
      setShowConfirm(false);
    }
  }, [open]);

  useEffect(() => {
    if (isClosing) {
      document.querySelector('.global-delete-modal .p-dialog')?.classList.add('dialog-closing');
      document.querySelector('.global-delete-modal')?.classList.add('mask-closing');
    }
  }, [isClosing]);

  const handleClose = () => {
    if (loading) return;
    setIsClosing(true);
    setTimeout(() => {
      onClose();
      setIsClosing(false);
      setShowConfirm(false);
    }, 400);
  };

  const handleDeleteClick = () => setShowConfirm(true);
  const handleCancelConfirm = () => setShowConfirm(false);
  const handleConfirm = () => { onConfirm(); setShowConfirm(false); };

  return (
    <>
      <style>{`
        .global-delete-modal {
          backdrop-filter: blur(4px);
          background-color: rgba(0,0,0,0.25) !important;
          animation: gdmMaskIn 0.35s ease;
        }
        .global-delete-modal.mask-closing {
          animation: gdmMaskOut 0.4s ease !important;
        }
        @keyframes gdmMaskIn  { from { opacity:0; } to { opacity:1; } }
        @keyframes gdmMaskOut { from { opacity:1; } to { opacity:0; } }

        .global-delete-modal .p-dialog {
          animation: gdmDialogIn 0.35s cubic-bezier(0.4,0,0.2,1);
          box-shadow: none !important;
          background: transparent !important;
          border: none !important;
          overflow: visible !important;
        }
        .global-delete-modal .p-dialog::before {
          content: '';
          position: absolute;
          inset: -15px;
          background: rgba(255,255,255,0.64);
          border-radius: 40px;
          z-index: -1;
          backdrop-filter: blur(20px);
          box-shadow: 0 8px 32px rgba(0,0,0,0.1);
        }
        .global-delete-modal .p-dialog.dialog-closing {
          animation: gdmDialogOut 0.4s cubic-bezier(0.4,0,0.2,1) !important;
        }
        @keyframes gdmDialogIn  { from { opacity:0; transform:scale(0.92) translateY(-20px); } to { opacity:1; transform:scale(1) translateY(0); } }
        @keyframes gdmDialogOut { from { opacity:1; transform:scale(1) translateY(0); } to { opacity:0; transform:scale(0.88) translateY(25px); } }

        @keyframes gdmConfirmIn { from { opacity:0; transform:scale(0.9); } to { opacity:1; transform:scale(1); } }
      `}</style>

      <Dialog
        visible={open && !isClosing}
        onHide={handleClose}
        showHeader={false}
        style={{ width: '90vw', maxWidth: '480px', borderRadius: '32px', overflow: 'visible', boxShadow: 'none' }}
        contentStyle={{ padding: '32px', background: 'white', borderRadius: '20px', boxShadow: '0 25px 50px rgba(0,0,0,0.15)' }}
        modal dismissableMask draggable={false} resizable={false} blockScroll
        maskClassName="global-delete-modal"
      >
        {/* Close Button */}
        <button
          onClick={handleClose}
          disabled={loading}
          style={{
            position: 'absolute', top: '20px', right: '20px',
            background: 'none', border: 'none', fontSize: '22px',
            cursor: loading ? 'not-allowed' : 'pointer', color: '#6b7280',
            width: '32px', height: '32px', display: 'flex',
            alignItems: 'center', justifyContent: 'center',
            transition: 'color 0.2s', padding: 0, zIndex: 10
          }}
          onMouseOver={e => (e.currentTarget.style.color = '#1f2937')}
          onMouseOut={e => (e.currentTarget.style.color = '#6b7280')}
        >×</button>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '20px', paddingBottom: '20px', borderBottom: '1px solid #e5e7eb' }}>
          <div style={{
            width: '54px', height: '54px', borderRadius: '14px', flexShrink: 0,
            background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 8px 24px rgba(239,68,68,0.3)', border: '3px solid white'
          }}>
            <i className="pi pi-trash" style={{ color: 'white', fontSize: '22px' }} />
          </div>
          <div>
            <div style={{ fontSize: '20px', fontWeight: 700, color: '#1e40af', lineHeight: 1.2, marginBottom: '4px' }}>
              {title}
            </div>
            <div style={{ fontSize: '13px', color: '#64748b', fontWeight: 500 }}>
              {subtitle}
            </div>
          </div>
        </div>

        {/* Optional custom content (e.g. item details) */}
        {children && (
          <div style={{ marginBottom: '20px' }}>
            {children}
          </div>
        )}

        {/* Action Buttons */}
        {!showConfirm ? (
          <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
            <GlobalButton label={cancelLabel} onClick={handleClose} variant="outline" size="medium" disabled={loading} />
            <GlobalButton label={confirmLabel} onClick={handleDeleteClick} variant="danger" size="medium" disabled={loading} />
          </div>
        ) : (
          // Mini confirm step
          <div style={{
            background: '#fef2f2', borderRadius: '12px', padding: '16px',
            border: '1px solid #fecaca', animation: 'gdmConfirmIn 0.2s ease'
          }}>
            <div style={{ fontSize: '14px', fontWeight: 600, color: '#dc2626', marginBottom: '12px', textAlign: 'center' }}>
              ⚠️ Are you sure? This cannot be undone.
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <GlobalButton label={loading ? 'Deleting...' : 'Yes, Delete'} onClick={handleConfirm} variant="danger" size="small" fullWidth loading={loading} disabled={loading} />
              <GlobalButton label="Cancel" onClick={handleCancelConfirm} variant="outline" size="small" fullWidth disabled={loading} />
            </div>
          </div>
        )}
      </Dialog>
    </>
  );
};

export default GlobalDeleteModal;